import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import {
  CogIcon,
  UserIcon,
  KeyIcon,
  GlobeAltIcon,
} from '@heroicons/react/24/outline';
import AdminLayout from '../../components/AdminLayout';

const AdminSettings = () => {
  const [settings, setSettings] = useState({
    siteName: 'armé',
    adminEmail: '',
    notifications: true,
    maintenanceMode: false,
    currency: 'DA',
    language: 'ar',
  });

  useEffect(() => {
    const saved = localStorage.getItem('adminSettings');
    if (saved) {
      try {
        setSettings((prev) => ({ ...prev, ...JSON.parse(saved) }));
      } catch {
        // ignore corrupted data
      }
    }
  }, []);

  const handleSave = () => {
    localStorage.setItem('adminSettings', JSON.stringify(settings));
    window.dispatchEvent(new CustomEvent('adminSettingsChanged', { detail: { settings } }));
    toast.success('Settings saved');
  };

  const inputClass =
    'w-full border border-warmgray-300 bg-white px-3 py-2.5 text-sm text-offblack placeholder:text-warmgray-400 focus:border-offblack focus:ring-0 focus:outline-none transition-colors';

  return (
    <AdminLayout title="Settings" subtitle="Manage your store preferences">
      <div className="max-w-3xl space-y-8">
        {/* General */}
        <section className="bg-white border border-warmgray-200 p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-9 h-9 flex items-center justify-center bg-warmgray-100">
              <CogIcon className="w-5 h-5 text-warmgray-600" />
            </div>
            <h2 className="font-display text-lg text-offblack">General Settings</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label htmlFor="siteName" className="block text-xs tracking-wider uppercase text-warmgray-500 mb-1.5">
                Site Name
              </label>
              <input
                id="siteName"
                type="text"
                value={settings.siteName}
                onChange={(e) => setSettings({ ...settings, siteName: e.target.value })}
                className={inputClass}
              />
            </div>

            <div>
              <label htmlFor="currency" className="block text-xs tracking-wider uppercase text-warmgray-500 mb-1.5">
                Currency
              </label>
              <select
                id="currency"
                value={settings.currency}
                onChange={(e) => setSettings({ ...settings, currency: e.target.value })}
                className={inputClass}
              >
                <option value="DA">DA (Algerian Dinar)</option>
                <option value="USD">USD (US Dollar)</option>
                <option value="EUR">EUR (Euro)</option>
              </select>
            </div>
          </div>
        </section>

        {/* Admin Account */}
        <section className="bg-white border border-warmgray-200 p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-9 h-9 flex items-center justify-center bg-warmgray-100">
              <UserIcon className="w-5 h-5 text-warmgray-600" />
            </div>
            <h2 className="font-display text-lg text-offblack">Admin Account</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label htmlFor="adminEmail" className="block text-xs tracking-wider uppercase text-warmgray-500 mb-1.5">
                Admin Email
              </label>
              <input
                id="adminEmail"
                type="email"
                value={settings.adminEmail}
                onChange={(e) => setSettings({ ...settings, adminEmail: e.target.value })}
                className={inputClass}
                placeholder="admin@example.com"
              />
            </div>

            <div>
              <label className="block text-xs tracking-wider uppercase text-warmgray-500 mb-1.5">
                Change Password
              </label>
              <button className="w-full flex items-center justify-center gap-2 border border-warmgray-300 px-3 py-2.5 text-sm text-offblack hover:bg-warmgray-50 transition-colors">
                <KeyIcon className="w-4 h-4" />
                Update Password
              </button>
            </div>
          </div>
        </section>

        {/* System */}
        <section className="bg-white border border-warmgray-200 p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-9 h-9 flex items-center justify-center bg-warmgray-100">
              <GlobeAltIcon className="w-5 h-5 text-warmgray-600" />
            </div>
            <h2 className="font-display text-lg text-offblack">System Settings</h2>
          </div>

          <div className="space-y-5">
            {/* Email Notifications */}
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-medium text-offblack">Email Notifications</h3>
                <p className="text-xs text-warmgray-500 mt-0.5">Receive notifications about orders and updates</p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={settings.notifications}
                onClick={() => setSettings({ ...settings, notifications: !settings.notifications })}
                className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer items-center border-2 border-transparent transition-colors ${
                  settings.notifications ? 'bg-offblack' : 'bg-warmgray-300'
                }`}
              >
                <span
                  className={`pointer-events-none inline-block h-4 w-4 bg-white shadow transition-transform ${
                    settings.notifications ? 'translate-x-5' : 'translate-x-0.5'
                  }`}
                />
              </button>
            </div>

            <div className="border-t border-warmgray-100" />

            {/* Maintenance Mode */}
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-medium text-offblack">Maintenance Mode</h3>
                <p className="text-xs text-warmgray-500 mt-0.5">Put the website in maintenance mode</p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={settings.maintenanceMode}
                onClick={() => setSettings({ ...settings, maintenanceMode: !settings.maintenanceMode })}
                className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer items-center border-2 border-transparent transition-colors ${
                  settings.maintenanceMode ? 'bg-red-600' : 'bg-warmgray-300'
                }`}
              >
                <span
                  className={`pointer-events-none inline-block h-4 w-4 bg-white shadow transition-transform ${
                    settings.maintenanceMode ? 'translate-x-5' : 'translate-x-0.5'
                  }`}
                />
              </button>
            </div>
          </div>
        </section>

        {/* Save */}
        <div className="flex justify-end">
          <button
            onClick={handleSave}
            className="bg-offblack text-offwhite px-8 py-3 text-sm tracking-wider uppercase hover:bg-charcoal transition-colors"
          >
            Save Settings
          </button>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminSettings;
